"use client";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import {
  CartType,
  CheckoutCookie,
} from "@/lib/ecommerce/commercetools/client-only/constants";
import { updateCookieExpiry } from "@/lib/ecommerce/commercetools/client-only/updateCookieExpiry";

/**
 * Cart cookie validation utility
 *
 * Checks if cart cookies need to be cleared based on the following rules:
 * - If cart type is 'C' (customer cart) but user is not logged in
 * - AND user doesn't have an lms_sessionid cookie
 * - Then clear both cart cookies (chk_cart_id and chk_cart_type)
 */
export const validateAndCleanCartCookies = (isLoggedIn: boolean): void => {
  try {
    // Get cart cookies
    const cartType = Cookies.get(CheckoutCookie.CartType) as CartType;

    // Get authentication cookies
    const lmsSessionId = Cookies.get("lms_sessionid");

    // Check if cart is type 'C' (customer cart)
    const isCustomerCart = cartType === CartType.Customer;

    // If cart is customer type but user is not logged in and no LMS session
    if (isCustomerCart && !isLoggedIn && !lmsSessionId) {
      // Clear both cart cookies
      Cookies.remove(CheckoutCookie.CartId);
      Cookies.remove(CheckoutCookie.CartType);
    }
  } catch (error) {
    // Log error but don't throw - this is a cleanup operation
    console.error("Error validating cart cookies:", error);
  }
};

/**
 * Hook to validate cart cookies on route changes and component mounts
 * This ensures cart cookies are validated whenever the user navigates
 */
export const useCartCookieValidation = (): void => {
  const pathname = usePathname();
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    // Validate cookies on every route change including initial load
    updateCookieExpiry();
    validateAndCleanCartCookies(isLoggedIn);
  }, [pathname, isLoggedIn]);
};
